/**
 * Every service the site offers, in one place.
 *
 * The /services hub and each /services/<slug> detail page read from here,
 * and App.js registers one route per entry — adding a service is one new
 * object below plus one <url> line in public/sitemap.xml (the prerender
 * warns if that line is missing).
 *
 * Content is English only for now; other languages show it as-is.
 *
 * Fields
 *  slug          URL segment: /services/<slug>
 *  category      groups the hub grid — one of SERVICE_CATEGORIES
 *  visual        3D artwork: { shape, colors: [surface, light, light] } —
 *                see components/AbstractVisual for the shapes
 *  name          short name (cards, breadcrumbs, nav)
 *  title         the page <h1>
 *  summary       one or two sentences for hub and related-service cards
 *  seoTitle      <title> without the " | MLMind" suffix — keep it ≤ 51 chars
 *  seoDescription meta description, ~150–160 chars
 *  tagline       line under the h1
 *  lead          hero paragraph — unique copy, not reused on cards
 *  statement     large headline opening the explanation
 *  intro         two paragraphs: what it is, and how we approach it
 *  deepDive      optional — { title, items: [{ title, text: [paragraphs] }] },
 *                a longer explanation shown after the intro (English only)
 *  offerings     what we build under this service
 *  useCases      when it is the right fit
 *  process       four delivery steps, written for this service
 *  stack         technologies we use for it
 *  caseStudy     optional — a project title from data/projects.js
 *  faq           questions a buyer actually asks (also FAQPage JSON-LD)
 *  related       slugs of three neighbouring services
 */

export const SERVICE_CATEGORIES = [
  { id: 'ai', label: 'AI & Machine Learning' },
  { id: 'data', label: 'Data & Infrastructure' },
  { id: 'product', label: 'Product Engineering' },
  { id: 'strategy', label: 'Design & Strategy' },
]

const services = [
  {
    slug: 'ai-development',
    category: 'ai',
    visual: { shape: 'ribbon', colors: ['#3b2a9e', '#e7ff02', '#a855f7'] },
    name: 'AI & Machine Learning Development',
    title: 'AI & machine learning development',
    summary:
      'Custom machine learning models trained on your data — forecasting, classification, scoring and detection that plug into the systems you already run.',
    seoTitle: 'Custom AI & Machine Learning Development',
    seoDescription:
      'Custom machine learning development: predictive models, classification, anomaly detection and forecasting trained on your data and deployed into production.',
    tagline: 'Models trained on your data, built to run in production.',
    lead:
      'Most companies already hold the data to predict their next quarter, their riskiest accounts or their busiest week. We turn that history into models that answer those questions every day.',
    statement: 'Machine learning that earns its place in your business.',
    intro: [
      'Machine learning finds patterns in historical data and uses them to make predictions about new cases: which customer is about to leave, how much stock you will need next month, which transaction looks like fraud, which scan needs a doctor’s attention first. Off-the-shelf tools cover generic problems; a custom model is worth it when the problem, the data or the accuracy you need is specific to your business.',
      'We start from the decision the model should improve and the data you already have, not from an algorithm. Then we build the simplest model that meets the target, prove it on held-out data, and wire it into your product or workflow so the prediction reaches the person who acts on it.',
    ],
    offerings: [
      { title: 'Predictive analytics', text: 'Demand and sales forecasting, churn prediction, lead and credit scoring built on your historical records.' },
      { title: 'Classification & detection', text: 'Models that sort documents, tickets, transactions or images into the categories your team works with.' },
      { title: 'Anomaly & fraud detection', text: 'Spotting unusual transactions, sensor readings or behaviour early, with alerts your team can act on.' },
      { title: 'Recommendation systems', text: 'Personalised product, content and next-best-action suggestions based on behaviour and purchase history.' },
      { title: 'Optimization', text: 'Pricing, scheduling, routing and resource allocation models that find better answers than rules of thumb.' },
      { title: 'Model audit & improvement', text: 'Reviewing an existing model for accuracy, bias and drift, then retraining or rebuilding what does not hold up.' },
    ],
    useCases: [
      'You have months or years of data and suspect it can predict something valuable',
      'A manual review or decision takes your team hours and follows recognisable patterns',
      'Rule-based logic has grown unmanageable and still misses edge cases',
      'You have a model in a notebook that never made it into production',
    ],
    process: [
      { title: 'Data audit', text: 'We check what your data can actually support — volume, quality, labels and leakage — before anyone promises accuracy.' },
      { title: 'Baseline & prototype', text: 'A simple baseline, then a working model in 3–6 weeks, measured against the metric agreed at the start.' },
      { title: 'Integration', text: 'An API or batch job that puts predictions where decisions are made: your app, CRM, dashboard or back office.' },
      { title: 'Monitoring & retraining', text: 'Accuracy and data-drift tracking in production, with a retraining routine so the model stays useful.' },
    ],
    stack: ['Python', 'scikit-learn', 'PyTorch', 'TensorFlow', 'XGBoost', 'Pandas', 'FastAPI', 'MLflow'],
    caseStudy: 'ArchiML',
    faq: [
      {
        question: 'How much data do we need for a custom machine learning model?',
        answer: 'It depends on the problem, but many useful models start from a few thousand labelled examples. We check your data in the audit phase and tell you plainly if it is not enough — and what it would take to collect more.',
      },
      {
        question: 'How long does it take to build a machine learning model?',
        answer: 'A working prototype usually takes 3–6 weeks. Taking it to production with integration and monitoring typically takes 3–4 months from kickoff.',
      },
      {
        question: 'Who owns the model and the code?',
        answer: 'You do. The code, the trained model weights and the data pipelines are handed over to you in full.',
      },
    ],
    related: ['generative-ai', 'data-engineering', 'mlops'],
  },
  {
    slug: 'generative-ai',
    category: 'ai',
    visual: { shape: 'loop', colors: ['#5b21b6', '#e7ff02', '#60a5fa'] },
    name: 'Generative AI & LLM Solutions',
    title: 'Generative AI & LLM solutions',
    summary:
      'Applications built on large language models — drafting, summarising, extracting and answering — with the guardrails a business needs.',
    seoTitle: 'Generative AI & LLM Development Services',
    seoDescription:
      'Generative AI development: LLM-powered apps for document processing, content drafting, summarisation and internal copilots — secure, evaluated and cost-controlled.',
    tagline: 'Large language models, put to work on real business tasks.',
    lead:
      'We build tools on GPT, Claude, Llama and other models that read, draft and summarise alongside your staff — scored on your own examples before anyone relies on them.',
    statement: 'Generative AI that does a job, not a demo.',
    intro: [
      'Large language models (LLMs) such as GPT, Claude, Gemini and Llama can read, write, summarise and reason over text. That makes them useful for work that used to need a person for every item: drafting replies, pulling fields out of contracts, summarising long reports, turning notes into structured records, or answering questions about your own documents.',
      'The hard part is not calling the model — it is making the output reliable. We design the prompts, the data flow and the checks around the model, measure quality on your real examples, keep costs predictable, and choose between hosted APIs and self-hosted open models based on your privacy requirements.',
    ],
    offerings: [
      { title: 'LLM application development', text: 'Web and internal tools built around a language model, from the interface to the prompt and data layers.' },
      { title: 'Document processing', text: 'Extracting structured data from invoices, contracts, forms and reports — with confidence checks and human review.' },
      { title: 'Content & drafting assistants', text: 'Tools that draft emails, descriptions, reports and replies in your tone, for a person to review and send.' },
      { title: 'Internal copilots', text: 'Assistants that help your team search, summarise and act inside the tools they already use.' },
      { title: 'Fine-tuning & model selection', text: 'Choosing the right model for cost, speed and privacy, and fine-tuning open models when prompting is not enough.' },
      { title: 'Evaluation & guardrails', text: 'Test sets, automatic quality scoring, content filters and fallbacks so output quality is measured, not assumed.' },
    ],
    useCases: [
      'Your team spends hours reading, summarising or re-typing documents',
      'You want to add an AI feature to your product but need it to be dependable',
      'Customer or internal requests follow patterns a model could draft answers for',
      'Data privacy rules mean you cannot simply paste company data into a public chatbot',
    ],
    process: [
      { title: 'Collect real examples', text: 'We gather genuine inputs and the outputs your team would accept — the benchmark every version is scored against.' },
      { title: 'Model & prompt design', text: 'Models compared on quality, speed, cost and privacy, then prompts and output formats designed around the task.' },
      { title: 'Guardrails & review', text: 'Validation checks, content filters and a human approval step wherever a mistake would be expensive.' },
      { title: 'Launch & cost tracking', text: 'Released to a first group of users, with quality scores and cost per request visible on a dashboard.' },
    ],
    stack: ['OpenAI', 'Anthropic Claude', 'Llama', 'Mistral', 'LangChain', 'LlamaIndex', 'Python', 'FastAPI'],
    faq: [
      {
        question: 'Is our data safe when using an LLM?',
        answer: 'We use API providers under business terms that exclude training on your data, or deploy open-source models on infrastructure you control when data must not leave your environment.',
      },
      {
        question: 'How do you stop the model from making things up?',
        answer: 'We ground answers in your own data (see RAG), constrain the output format, add automatic checks and keep a human review step where mistakes are costly. Quality is measured on a test set before launch.',
      },
      {
        question: 'What does it cost to run an LLM application?',
        answer: 'Running costs depend on usage and model choice. We estimate per-request cost during scoping and design with caching and smaller models where they are good enough, so the bill stays predictable.',
      },
    ],
    related: ['rag', 'ai-agents', 'nlp-chatbots'],
  },
  {
    slug: 'rag',
    category: 'ai',
    visual: { shape: 'weave', colors: ['#1e3a8a', '#e7ff02', '#818cf8'] },
    name: 'RAG Systems',
    title: 'RAG systems: AI that answers from your own data',
    summary:
      'Retrieval-augmented generation — assistants that search your documents and answer with sources, instead of guessing.',
    seoTitle: 'RAG Development: Retrieval-Augmented Generation',
    seoDescription:
      'RAG development services: AI assistants that search your documents, knowledge base and databases and answer with cited sources. Secure, accurate, measurable.',
    tagline: 'Answers grounded in your documents, with the sources to prove it.',
    lead:
      'Point an assistant at your manuals, policies and past tickets. It finds the right passage, answers in plain language and links to the source, so every reply can be checked.',
    statement: 'An AI assistant that knows your business.',
    intro: [
      'Retrieval-augmented generation (RAG) connects a language model to your own information. When someone asks a question, the system first searches your documents, wiki, tickets or database for the most relevant passages, then asks the model to answer using only those passages — and shows where each answer came from. The model stays general; the knowledge stays yours and up to date.',
      'A RAG system is only as good as its retrieval. We spend most of the effort there: cleaning and splitting documents sensibly, choosing embeddings and hybrid search, re-ranking results, respecting who is allowed to see what, and measuring answer accuracy on real questions from your team before anyone relies on it.',
    ],
    deepDive: {
      title: 'What goes into a RAG system people actually trust',
      items: [
        {
          title: 'Retrieval comes first',
          text: [
            'Most disappointing RAG assistants fail before the language model is even involved: the search step returns the wrong passage, so the model answers the wrong question. We treat retrieval as the core of the product.',
            'That means splitting documents along their real structure — sections, tables, clauses — rather than at a fixed length, combining semantic and keyword search so product codes and names are found exactly, and re-ranking the results before the model sees them.',
          ],
        },
        {
          title: 'Grounded answers, with sources',
          text: [
            'The assistant is instructed to answer only from the passages it retrieved, and every answer links to the document and section it used, so anyone can check it in one click.',
            'When your documents do not contain the answer, it says so and suggests who to ask. An honest “I don’t know” is what makes people trust the answers it does give.',
          ],
        },
        {
          title: 'Permissions built in',
          text: [
            'A knowledge assistant must not become a way around your access rules. Each document carries its permissions into the index, and search is filtered by the person asking — so an employee never receives an answer drawn from a file they could not open themselves.',
            'For customer-facing assistants, only content approved for publication is indexed at all.',
          ],
        },
        {
          title: 'Measured before launch',
          text: [
            'Before anyone relies on it, we build a test set from real questions your team asks, with the correct answers. Every version is scored on it: did search find the right passage, and did the answer stay faithful to it.',
            'After launch, unanswered questions and thumbs-down feedback feed the next round of tuning, and the index updates automatically as documents change.',
          ],
        },
        {
          title: 'Your data stays yours',
          text: [
            'We choose models and hosting to match your rules: hosted APIs with no training on your data, EU-region infrastructure for GDPR, or open models running entirely inside your own cloud when documents must not leave it.',
          ],
        },
        {
          title: 'Where it pays off first',
          text: [
            'Support teams answering from manuals and past tickets; new employees finding policies without asking around; sales and operations staff searching contracts, specifications and procedures; and regulated work where every answer has to point to its source.',
            'It also works in Georgian and Russian as well as English — tested on real questions in each language.',
          ],
        },
      ],
    },
    offerings: [
      { title: 'Knowledge-base assistants', text: 'Internal Q&A over policies, manuals, wikis and past tickets, so staff stop searching and asking around.' },
      { title: 'Customer-facing support bots', text: 'Answers from your help centre and product docs, with sources and a hand-off to a human when needed.' },
      { title: 'Document ingestion pipelines', text: 'Loading PDFs, Word, web pages, Confluence, Notion or databases, and keeping the index in sync as they change.' },
      { title: 'Vector & hybrid search', text: 'Embeddings, keyword search and re-ranking combined for accurate retrieval, on pgvector, Qdrant, Pinecone or Elasticsearch.' },
      { title: 'Access control', text: 'Answers that respect permissions, so a user only ever sees content they are allowed to read.' },
      { title: 'Accuracy evaluation', text: 'Test questions with known answers, retrieval and faithfulness scores, and dashboards to track quality over time.' },
    ],
    useCases: [
      'Knowledge is spread across documents that people struggle to search',
      'Support agents answer the same questions from the same manuals every day',
      'You need an AI assistant that cites sources, not one that sounds confident',
      'Your information changes too often to retrain or fine-tune a model',
    ],
    process: [
      { title: 'Source mapping', text: 'We list the documents and systems to include, who may access them, and collect real questions with correct answers.' },
      { title: 'Retrieval prototype', text: 'Ingestion, chunking and search tuned until the right passages come back for your test questions.' },
      { title: 'Assistant & interface', text: 'Answer generation with citations, in a web app, Slack, Teams or your own product.' },
      { title: 'Evaluate & operate', text: 'Accuracy measured before launch, index updates automated, and feedback from users fed back into tuning.' },
    ],
    stack: ['LangChain', 'LlamaIndex', 'pgvector', 'Qdrant', 'Pinecone', 'Elasticsearch', 'OpenAI', 'Python'],
    faq: [
      {
        question: 'What is the difference between RAG and fine-tuning?',
        answer: 'Fine-tuning changes how a model writes and behaves; RAG gives it facts to work from at question time. For answering questions about your own, changing documents, RAG is usually cheaper, easier to keep current and able to cite sources.',
      },
      {
        question: 'Which document formats can a RAG system use?',
        answer: 'PDF, Word, Excel, HTML, Markdown, and content in tools such as Confluence, Notion, Google Drive, SharePoint or your own database. Scanned documents can be included with OCR.',
      },
      {
        question: 'How accurate is a RAG assistant?',
        answer: 'We measure it on a set of real questions with known answers before launch and report the numbers. When the answer is not in your documents, the assistant is built to say so rather than guess.',
      },
      {
        question: 'How do you stop the assistant from making things up?',
        answer: 'By controlling what it may answer from: only retrieved passages, a citation on every answer, and an explicit instruction to decline when the sources do not cover the question. We then test it with questions that have no answer in your documents, to confirm it declines instead of guessing.',
      },
      {
        question: 'How do you handle who can see what?',
        answer: 'Permissions from the source systems travel with each document into the search index, and every search is filtered by the identity of the person asking. People only get answers from content they could already open.',
      },
      {
        question: 'How long does it take and what does it cost?',
        answer: 'A focused assistant over one well-defined set of documents is typically a working prototype in 3–6 weeks. Cost depends on the number and state of the sources, the permission model and where it must run; our estimator gives a first range in a few minutes, and the scoping call that follows is free.',
      },
      {
        question: 'Can it run without sending our documents to OpenAI?',
        answer: 'Yes. We can use hosted models under terms that exclude training on your data, keep everything in EU-region cloud infrastructure, or deploy open models entirely inside your own environment.',
      },
    ],
    related: ['rag-proof-of-concept', 'generative-ai', 'nlp-chatbots'],
  },
  {
    slug: 'rag-proof-of-concept',
    category: 'ai',
    visual: { shape: 'orb', colors: ['#0e2a47', '#e7ff02', '#22d3ee'] },
    name: 'RAG Proof of Concept',
    title: 'RAG proof of concept on your own documents',
    summary:
      'A fixed-price, 2–4 week test on your real contracts, policies or manuals: a working prototype, a measured quality report and a clear go or no-go before you commit to a full build.',
    seoTitle: 'RAG Proof of Concept: Fixed Price, 2–4 Weeks',
    seoDescription:
      'Find out whether an AI assistant can really answer from your documents. Fixed-price RAG proof of concept in 2–4 weeks: working prototype, benchmark report, go/no-go.',
    tagline: 'Know whether it works on your documents before you pay for a full build.',
    lead:
      'Demos on sample PDFs prove very little. Contracts, regulations and technical manuals have tables, cross-references, scanned pages and exceptions that break generic assistants. In two to four weeks we build a working prototype on a representative slice of your own documents and measure how well it answers — so the decision to go further rests on evidence.',
    statement: 'An honest answer in weeks, not a six-month experiment.',
    intro: [
      'A proof of concept answers one question: can retrieval-augmented generation answer your team’s real questions from your real documents, accurately enough to be useful? We agree the questions and the success criteria with you at the start, build the smallest system that can answer them, and test it against those criteria — not against questions we picked because they work.',
      'You get three things at the end: a prototype your team can try, a benchmark report that shows accuracy on the agreed questions with the failures named and explained, and a recommendation. If the answer is go, the report doubles as the plan and estimate for production. If it is no-go, you learn why for the price of a few weeks instead of a failed project.',
    ],
    offerings: [
      { title: 'Scoping & question set', text: 'We agree the document set, 50–150 real questions with expected answers, and what “good enough” means for your use case.' },
      { title: 'Document processing', text: 'Parsing that respects the structure of your documents — sections, tables, clauses, scans — because retrieval quality starts here.' },
      { title: 'Working prototype', text: 'A simple chat or search interface over your documents, with answers that cite the passage they came from.' },
      { title: 'Benchmark report', text: 'Accuracy on the agreed questions, retrieval quality, response time and cost per question, with each failure case shown and explained.' },
      { title: 'Go / no-go recommendation', text: 'A straight recommendation, and for a go: the architecture, effort and budget for a production system.' },
      { title: 'Your choice of hosting', text: 'Hosted models under terms that exclude training on your data, EU-region cloud, or open models inside your own environment.' },
    ],
    useCases: [
      'You want an AI assistant over contracts, regulations or manuals but are not sure it can be accurate enough',
      'A vendor demo looked good, and you want to see results on your own documents before buying',
      'Leadership needs evidence and a budget before approving an AI project',
      'An earlier chatbot attempt gave vague or wrong answers and you want to know why',
    ],
    process: [
      { title: 'Week 1: scope & data', text: 'Agree the questions and success criteria, receive a representative document sample under NDA, and set up processing.' },
      { title: 'Week 2: build', text: 'Retrieval, answer generation with citations, and a simple interface for your team to try.' },
      { title: 'Week 3: measure', text: 'Run the full question set, analyse every failure, and tune what can be tuned within the scope.' },
      { title: 'Week 4: report & decision', text: 'Walk-through of the prototype and benchmark, and a go or no-go recommendation with a production plan.' },
    ],
    stack: ['Python', 'FastAPI', 'pgvector', 'Elasticsearch', 'Docling', 'OpenAI', 'Anthropic Claude', 'AWS Bedrock', 'Open-source LLMs'],
    faq: [
      {
        question: 'Why a fixed price?',
        answer: 'Because the scope is fixed: a defined document sample, an agreed question set and a report at the end. You know the cost before we start, and there are no open-ended hours.',
      },
      {
        question: 'How many documents do you need?',
        answer: 'A representative sample rather than everything — typically a few hundred pages that include the hard cases: tables, scans, cross-references and exceptions. A good sample predicts production behaviour far better than a large easy one.',
      },
      {
        question: 'What happens after the proof of concept?',
        answer: 'If the result is go, the report already contains the architecture, effort and budget for production, and the prototype becomes its starting point. You are free to build it with us or with anyone else.',
      },
      {
        question: 'Is our data safe?',
        answer: 'We sign an NDA before any document is shared, can work in EU-region infrastructure or entirely in your environment, and use model providers whose terms exclude training on your data. Everything is deleted at the end unless you ask us to keep it.',
      },
    ],
    related: ['rag', 'generative-ai', 'ai-consulting'],
  },
  {
    slug: 'ai-agents',
    category: 'ai',
    visual: { shape: 'ring', colors: ['#312e81', '#e7ff02', '#8b5cf6'] },
    name: 'AI Agents & Automation',
    title: 'AI agents & process automation',
    summary:
      'AI agents that carry out multi-step tasks across your tools — research, triage, data entry and follow-ups — with a human in control.',
    seoTitle: 'AI Agent Development & Process Automation',
    seoDescription:
      'AI agent development: LLM agents that use your tools and APIs to automate multi-step work like triage, research, data entry and reporting, with human approval.',
    tagline: 'Software that takes the next step, not just the next word.',
    lead:
      'An agent reads a request, decides which tools to use and carries the task through your CRM, inbox or database — logging every step and pausing for approval where it matters.',
    statement: 'Automate the work between your systems.',
    intro: [
      'An AI agent is a language model that can use tools: it reads a request, decides what to do, calls APIs or searches data, checks the result and continues until the task is done. That makes it possible to automate work that follows a goal rather than a fixed script — triaging incoming requests, researching a lead, reconciling records, or preparing a report from several systems.',
      'Agents are powerful and easy to over-trust. We keep them narrow and observable: a clear set of permitted actions, logs of every step, limits on cost and scope, and human approval before anything irreversible. Where a plain workflow automation is enough, we will tell you and build that instead.',
    ],
    offerings: [
      { title: 'Task agents', text: 'Agents that complete a defined job end to end — qualify a lead, answer a request, prepare a quote.' },
      { title: 'Tool & API integration', text: 'Connecting agents to your CRM, helpdesk, email, calendar, database and internal APIs safely.' },
      { title: 'Workflow automation', text: 'Combining deterministic automation with AI steps where judgement is needed, in n8n, Zapier or custom code.' },
      { title: 'Multi-agent systems', text: 'Several specialised agents that plan, execute and review each other’s work for complex processes.' },
      { title: 'Human-in-the-loop design', text: 'Approval steps, escalation rules and review queues so people stay in charge of important decisions.' },
      { title: 'Monitoring & cost control', text: 'Step-by-step traces, success rates and spend limits so you always know what the agents did and why.' },
    ],
    useCases: [
      'A process involves copying information between several tools by hand',
      'Requests arrive in free text and need reading, sorting and routing',
      'Your team repeats the same research or preparation steps for every case',
      'You want to automate more than rigid rules can handle',
    ],
    process: [
      { title: 'Pick one process', text: 'We map a single repetitive workflow step by step and agree what the agent may and may not do.' },
      { title: 'Connect the tools', text: 'Scoped API access to the systems involved, tested in a sandbox before the agent touches live data.' },
      { title: 'Supervised runs', text: 'The agent works alongside your team, proposing each action for approval until its accuracy is proven.' },
      { title: 'Gradual autonomy', text: 'Low-risk steps become fully automatic; high-stakes ones keep a human sign-off. Traces and spend stay visible.' },
    ],
    stack: ['OpenAI', 'Anthropic Claude', 'LangGraph', 'n8n', 'Python', 'Node.js', 'PostgreSQL', 'REST APIs'],
    faq: [
      {
        question: 'What is the difference between an AI agent and a chatbot?',
        answer: 'A chatbot answers questions. An agent takes actions — it can call your systems, update records and complete multi-step tasks towards a goal.',
      },
      {
        question: 'Is it safe to let an AI agent act in our systems?',
        answer: 'We give each agent the minimum permissions it needs, log every action, cap spending and require human approval for anything irreversible, such as payments or sending messages to customers.',
      },
      {
        question: 'Which processes are good candidates for agents?',
        answer: 'Frequent, repetitive tasks that follow a goal but vary in detail — request triage, lead research, data entry from documents, report preparation. We help you pick one with clear value to start with.',
      },
    ],
    related: ['generative-ai', 'rag', 'ai-consulting'],
  },
  {
    slug: 'computer-vision',
    category: 'ai',
    visual: { shape: 'orb', colors: ['#0e7490', '#e7ff02', '#6366f1'] },
    name: 'Computer Vision',
    title: 'Computer vision development',
    summary:
      'Software that sees — image and video analysis for detection, segmentation, inspection, OCR and medical imaging.',
    seoTitle: 'Computer Vision Development Services',
    seoDescription:
      'Computer vision development: object detection, image segmentation, visual inspection, OCR and medical image analysis models, deployed on cloud or edge devices.',
    tagline: 'Turning images and video into decisions.',
    lead:
      'From tumour segmentation on MRI scans to reading walls and doors off architectural drawings, we train vision models that do in milliseconds what takes a specialist minutes.',
    statement: 'Teach your software to see.',
    intro: [
      'Computer vision lets software interpret images and video: find and count objects, outline regions, read text, spot defects, recognise products or flag anomalies in medical scans. Tasks that need a person to look at every image can be done in milliseconds, consistently, around the clock.',
      'We have built vision systems for healthcare, where accuracy and explainability matter. We handle the full pipeline — collecting and labelling images, training and validating models, and deploying them where they need to run, whether that is a cloud API, a hospital server or a camera on the factory floor.',
    ],
    offerings: [
      { title: 'Object detection & tracking', text: 'Finding, counting and following objects or people in images and live video.' },
      { title: 'Image segmentation', text: 'Pixel-level outlines of regions — tumours, defects, rooms on a floor plan, crops in a field.' },
      { title: 'Medical image analysis', text: 'Models that assist with MRI, CT, X-ray and pathology images to help clinicians read scans faster.' },
      { title: 'Visual inspection', text: 'Automated quality control that catches defects on production lines more consistently than manual checks.' },
      { title: 'OCR & document vision', text: 'Reading text, tables and handwriting from scans, photos, drawings and forms.' },
      { title: 'Edge deployment', text: 'Optimised models that run on cameras, phones and on-premise devices without a cloud round trip.' },
    ],
    useCases: [
      'People review images or video by eye, and it is slow or inconsistent',
      'You need to extract information from drawings, scans or photos at scale',
      'Quality control misses defects or cannot keep up with production',
      'You have imaging data and want to assist specialists, not replace them',
    ],
    process: [
      { title: 'Image review', text: 'We study samples from your real conditions — lighting, angles, devices, awkward edge cases — before choosing an approach.' },
      { title: 'Labelling', text: 'A labelling guide and an annotated dataset, produced with your specialists wherever domain knowledge matters.' },
      { title: 'Train & validate', text: 'Models built on strong pre-trained backbones and tested on images they have never seen, with every error reviewed.' },
      { title: 'Deploy where needed', text: 'Served from the cloud, an on-premise server or directly on cameras and phones, depending on speed and privacy.' },
    ],
    stack: ['PyTorch', 'OpenCV', 'YOLO', 'Segment Anything', 'TensorFlow', 'ONNX', 'Python', 'Label Studio'],
    caseStudy: 'MedicMinder',
    faq: [
      {
        question: 'How many images do we need to train a computer vision model?',
        answer: 'Starting from pre-trained models, a few hundred to a few thousand labelled images per category is often enough for a first version. We assess your images early and can organise labelling if needed.',
      },
      {
        question: 'Can computer vision run without an internet connection?',
        answer: 'Yes. We optimise models to run on edge devices, on-premise servers or phones, so images never have to leave your site.',
      },
      {
        question: 'Do you work with medical imaging data?',
        answer: 'Yes — MedicMinder, our medical image analysis platform, works with MRI, CT and X-ray images. We handle medical data with strict access control and anonymisation.',
      },
    ],
    related: ['ai-development', 'mlops', 'mobile-development'],
  },
  {
    slug: 'nlp-chatbots',
    category: 'ai',
    visual: { shape: 'loop', colors: ['#4c1d95', '#e7ff02', '#e879f9'] },
    name: 'NLP & Chatbots',
    title: 'NLP & chatbot development',
    summary:
      'Natural language processing and conversational assistants — support bots, review analysis, text classification and multilingual chat.',
    seoTitle: 'NLP & AI Chatbot Development Services',
    seoDescription:
      'NLP and AI chatbot development: customer support bots, product-selection assistants, review and sentiment analysis, and text classification in multiple languages.',
    tagline: 'Understand what customers write, and answer it well.',
    lead:
      'Make sense of thousands of messages, reviews and tickets at once, and answer customers on your site, in WhatsApp or in Telegram without making them wait.',
    statement: 'Conversations that actually help.',
    intro: [
      'Natural language processing (NLP) lets software understand text: its topic, its sentiment, the entities it mentions and what the writer wants. It powers chatbots that answer customers, systems that sort thousands of support tickets, and analysis that turns reviews into a list of what to fix next.',
      'Modern chatbots built on language models are far more capable than scripted decision trees — but they still need a clear job, access to the right information and a graceful way to hand over to a person. We build assistants for your website, app, WhatsApp, Telegram or Messenger, in the languages your customers use.',
    ],
    offerings: [
      { title: 'Customer support chatbots', text: 'Assistants that resolve common questions around the clock and pass complex cases to your team with context.' },
      { title: 'Product-selection assistants', text: 'Conversational shopping help that asks the right questions and recommends suitable products.' },
      { title: 'Review & sentiment analysis', text: 'Turning customer reviews and feedback into trends, top complaints and popular features.' },
      { title: 'Text classification & routing', text: 'Automatically tagging and routing emails, tickets and forms to the right team.' },
      { title: 'Entity extraction', text: 'Pulling names, dates, amounts and product references out of unstructured text.' },
      { title: 'Multilingual NLP', text: 'Models and bots that work across English, Georgian, Russian and other languages.' },
    ],
    useCases: [
      'Your support team answers the same questions many times a day',
      'You receive more feedback, reviews or tickets than anyone can read',
      'Customers need help choosing between many products or options',
      'You serve customers in several languages',
    ],
    process: [
      { title: 'Conversation audit', text: 'We read through real chats, emails or tickets to find the questions that come up most and where customers get stuck.' },
      { title: 'Knowledge & tone', text: 'Answers grounded in your help content and written in your brand voice, in every language you serve.' },
      { title: 'Channel integration', text: 'The assistant goes live on your website, app or messaging channels, with hand-off to your team built in.' },
      { title: 'Weekly review', text: 'Unanswered and escalated conversations are reviewed and used to widen what the assistant can handle.' },
    ],
    stack: ['Hugging Face', 'spaCy', 'OpenAI', 'Rasa', 'Python', 'Node.js', 'WhatsApp API', 'Telegram API'],
    faq: [
      {
        question: 'Where can the chatbot be deployed?',
        answer: 'On your website, in your mobile app, and on messaging channels such as WhatsApp, Telegram, Facebook Messenger, Slack or Microsoft Teams.',
      },
      {
        question: 'Can the chatbot hand over to a human?',
        answer: 'Yes. We design clear hand-off rules, and the conversation history travels with the case so your team does not have to ask the customer again.',
      },
      {
        question: 'Which languages do you support?',
        answer: 'Modern language models handle most major languages well. We test quality in each language you need, including Georgian and Russian.',
      },
    ],
    related: ['rag', 'georgian-language-ai', 'generative-ai'],
  },
  {
    slug: 'georgian-language-ai',
    category: 'ai',
    visual: { shape: 'weave', colors: ['#7f1d1d', '#e7ff02', '#f472b6'] },
    name: 'Georgian Language AI',
    title: 'Georgian language AI: speech, voice and chatbots',
    summary:
      'Speech recognition, voice assistants and chatbots that work in Georgian — tested on real Georgian phrases, not just switched on and hoped for.',
    seoTitle: 'Georgian Speech Recognition & AI Chatbots',
    seoDescription:
      'Georgian language AI from a team in Tbilisi: Georgian speech-to-text, text-to-speech voices, voice assistants and chatbots that understand Georgian customers.',
    tagline: 'AI that speaks Georgian as well as your staff do.',
    lead:
      'Most AI products are built for English first and Georgian last. We build and test voice and chat assistants for Georgian customers from the start — in Tbilisi, with native speakers checking every answer.',
    statement: 'Your customers speak Georgian. Your AI should too.',
    intro: [
      'Georgian is a small language for global AI vendors: fewer training examples, fewer voices and less testing than English or Russian. The major cloud speech services do support Georgian, but quality varies by provider, by vocabulary and by environment — a phone line, a noisy branch office and a quiet website chat all behave differently. A bot that works in an English demo can stumble on Georgian names, addresses, numbers and mixed Georgian–English speech.',
      'We start by testing several speech and language providers on your own phrases — product names, street names, amounts, the way your customers actually talk — and pick the combination that scores best. Then we build the assistant around it: a clear script where precision matters, a language model where flexibility helps, and a hand-off to a person when the AI is not sure.',
    ],
    offerings: [
      { title: 'Georgian speech-to-text', text: 'Transcription of calls, voice messages and live speech in Georgian, with custom vocabulary for your brands, products and places.' },
      { title: 'Georgian text-to-speech', text: 'Natural Georgian voices for announcements, phone menus, kiosks and assistants — chosen and tuned with native listeners.' },
      { title: 'Voice assistants', text: 'Assistants that listen and answer by voice in Georgian — on the phone, in an app, or on a physical kiosk or device.' },
      { title: 'Georgian chatbots', text: 'Website, Facebook Messenger, WhatsApp and Telegram assistants that understand Georgian, including Latin-script “translit” Georgian.' },
      { title: 'Georgian document processing', text: 'Extracting names, IDs, dates and amounts from Georgian forms, contracts and scanned documents.' },
      { title: 'Multilingual service', text: 'One assistant that answers in Georgian, English or Russian, depending on how the customer starts the conversation.' },
    ],
    useCases: [
      'Your call centre or support chat handles the same Georgian questions all day',
      'You need call recordings or meetings in Georgian turned into searchable text',
      'A marketing activation, kiosk or device should talk to visitors in Georgian',
      'An off-the-shelf chatbot you tried understood English well and Georgian badly',
    ],
    process: [
      { title: 'Collect real Georgian samples', text: 'Recordings, chats and phrases from your customers — the test set every provider and version is scored against.' },
      { title: 'Provider benchmark', text: 'Several speech and language services compared on accuracy, speed and cost for your vocabulary, not on a generic demo.' },
      { title: 'Build & native review', text: 'The assistant is built around the winning setup, and native speakers review its answers and voice before launch.' },
      { title: 'Launch & improve', text: 'Misheard words and unanswered questions are logged and fed back into vocabulary, prompts and scripts every week.' },
    ],
    stack: ['Azure Speech', 'Google Cloud Speech', 'Whisper', 'OpenAI', 'Anthropic Claude', 'Python', 'Node.js', 'WhatsApp API'],
    faq: [
      {
        question: 'Does speech recognition work well in Georgian?',
        answer: 'It works, but quality depends heavily on the provider, the vocabulary and the environment. That is why we benchmark several providers on your own recordings before building anything, and design a fallback — buttons or a text reply — for the cases speech gets wrong.',
      },
      {
        question: 'Can the assistant speak Georgian with a natural voice?',
        answer: 'Yes. Cloud providers offer neural Georgian voices; we compare them with native listeners and choose the one that fits your brand. For fixed phrases a recorded human voice is also an option.',
      },
      {
        question: 'Can one chatbot handle Georgian, English and Russian?',
        answer: 'Yes. The assistant detects the language the customer writes or speaks in and answers in the same language, using the same knowledge base.',
      },
      {
        question: 'Where is our data processed?',
        answer: 'We choose providers and regions to match your requirements, avoid storing voice recordings unless you need them, and can use self-hosted models where data must not leave your infrastructure.',
      },
    ],
    related: ['nlp-chatbots', 'rag', 'generative-ai'],
  },
  {
    slug: 'data-engineering',
    category: 'data',
    visual: { shape: 'weave', colors: ['#0f3b66', '#e7ff02', '#22c55e'] },
    name: 'Data Engineering & Analytics',
    title: 'Data engineering & analytics',
    summary:
      'Pipelines, warehouses and dashboards that turn scattered data into one reliable source — the foundation every AI project needs.',
    seoTitle: 'Data Engineering & Analytics Services',
    seoDescription:
      'Data engineering and analytics services: ETL pipelines, data warehouses, data quality and BI dashboards that give your team and your AI models reliable data.',
    tagline: 'Reliable data first. Everything else builds on it.',
    lead:
      'Before AI and before dashboards comes the plumbing: pipelines that pull data out of every tool you use, clean it and land it in one place your team can query.',
    statement: 'Good AI starts with good data.',
    intro: [
      'Most companies have plenty of data — spread across a CRM, spreadsheets, an ERP, web analytics and a production database that nobody wants to query. Data engineering brings it together: pipelines that collect and clean it automatically, a warehouse that stores it in one consistent shape, and dashboards that answer questions without a developer in the loop.',
      'This is also the step most AI projects skip and regret. We build data foundations that are useful today for reporting and ready tomorrow for machine learning, with quality checks so numbers can be trusted and documentation so your team can maintain them.',
    ],
    offerings: [
      { title: 'Data pipelines (ETL/ELT)', text: 'Automated, scheduled pipelines that move and transform data from every source you use.' },
      { title: 'Data warehouses', text: 'A single, well-modelled store on BigQuery, Snowflake, Redshift or PostgreSQL.' },
      { title: 'BI dashboards', text: 'Dashboards in Power BI, Looker Studio, Metabase or custom web apps that show the numbers that matter.' },
      { title: 'Data quality & governance', text: 'Validation, monitoring and clear ownership so data errors are caught before they reach decisions.' },
      { title: 'Data preparation for ML', text: 'Feature pipelines, labelling workflows and training datasets built for machine learning.' },
      { title: 'Web scraping & data collection', text: 'Collecting public and partner data at scale, legally and reliably.' },
    ],
    useCases: [
      'Reporting means exporting spreadsheets and combining them by hand',
      'Different teams quote different numbers for the same metric',
      'You want to start with AI but your data lives in many disconnected places',
      'Queries on your production database are slowing down your product',
    ],
    process: [
      { title: 'Source inventory', text: 'A list of every system holding relevant data, who owns it and how it can be accessed.' },
      { title: 'Data model', text: 'A clear structure for customers, orders, products — whatever your business revolves around — agreed with the people who use the numbers.' },
      { title: 'Pipelines & checks', text: 'Scheduled loads with validation rules, so broken or missing data raises an alert instead of producing a wrong report.' },
      { title: 'Dashboards & training', text: 'The first dashboards built on top, with documentation and a session so your team can extend them.' },
    ],
    stack: ['Python', 'SQL', 'dbt', 'Airflow', 'BigQuery', 'Snowflake', 'PostgreSQL', 'Power BI'],
    faq: [
      {
        question: 'Do we need a data warehouse before starting with AI?',
        answer: 'Not always, but you need reliable, accessible data. For many projects a focused pipeline for the data the model needs is enough to start; a warehouse becomes worth it as use grows.',
      },
      {
        question: 'Which data sources can you connect?',
        answer: 'Databases, CRMs, ERPs, spreadsheets, web analytics, payment systems, APIs and files. If it has an export or an API, we can usually build a pipeline for it.',
      },
      {
        question: 'Can our team maintain the pipelines afterwards?',
        answer: 'Yes. We use standard, well-documented tools, hand over the code and documentation, and can train your team.',
      },
    ],
    related: ['ai-development', 'mlops', 'ai-consulting'],
  },
  {
    slug: 'mlops',
    category: 'data',
    visual: { shape: 'ring', colors: ['#1f2a5a', '#e7ff02', '#38bdf8'] },
    name: 'MLOps & Cloud Infrastructure',
    title: 'MLOps & cloud infrastructure',
    summary:
      'The infrastructure that takes models from a notebook to production — training pipelines, deployment, monitoring and cost control on AWS, GCP or Azure.',
    seoTitle: 'MLOps & ML Infrastructure Services',
    seoDescription:
      'MLOps services: ML infrastructure, training pipelines, model deployment, monitoring and retraining on AWS, GCP and Azure — reliable, scalable and cost-efficient.',
    tagline: 'From a notebook to a service your business can rely on.',
    lead:
      'Versioned models, automated retraining, monitoring and a sensible cloud bill — the engineering that keeps a model accurate in month twelve, not just on demo day.',
    statement: 'Models are only useful in production.',
    intro: [
      'MLOps is the engineering practice that keeps machine learning working after the first demo: versioned data and models, automated training pipelines, reliable deployment, and monitoring that notices when accuracy slips because the world has changed. It is the difference between a model that impressed once and one your business depends on.',
      'We set up infrastructure proportional to your needs — a single well-monitored API for one model, or a full platform for a team shipping many — on AWS, Google Cloud, Azure or your own servers, with attention to reliability, security and the monthly bill.',
    ],
    offerings: [
      { title: 'Model deployment', text: 'Serving models as fast, scalable APIs or batch jobs, with zero-downtime updates.' },
      { title: 'Training pipelines', text: 'Reproducible, automated training with versioned data, code and models.' },
      { title: 'Monitoring & drift detection', text: 'Tracking latency, errors, accuracy and data drift, with alerts before users notice problems.' },
      { title: 'Cloud architecture', text: 'Secure, scalable infrastructure on AWS, GCP or Azure, defined as code.' },
      { title: 'GPU & cost optimisation', text: 'Right-sizing compute, using spot instances and model optimisation to cut inference costs.' },
      { title: 'On-premise & private deployment', text: 'Running models inside your own network when data cannot leave it.' },
    ],
    useCases: [
      'You have models that work in notebooks but not in production',
      'Deploying a new model version is manual, slow or risky',
      'Nobody notices when a model’s accuracy degrades',
      'Your cloud or GPU bill is growing faster than usage',
    ],
    process: [
      { title: 'Current-state review', text: 'How models are trained, shipped and watched today, and where that breaks or costs too much.' },
      { title: 'Pipeline setup', text: 'Reproducible training and deployment with versioned data, code and model artefacts.' },
      { title: 'Monitoring', text: 'Dashboards and alerts for latency, errors, prediction quality and data drift.' },
      { title: 'Cost & reliability tuning', text: 'Autoscaling, right-sized instances and lighter models wherever accuracy allows.' },
    ],
    stack: ['AWS', 'Google Cloud', 'Azure', 'Docker', 'Kubernetes', 'Terraform', 'MLflow', 'GitHub Actions'],
    faq: [
      {
        question: 'Which cloud providers do you work with?',
        answer: 'AWS, Google Cloud and Microsoft Azure, as well as on-premise servers. We recommend based on your existing setup, data location and costs.',
      },
      {
        question: 'Can you take over a model another team built?',
        answer: 'Yes. We review the model and code, then build the deployment, monitoring and retraining around it — or tell you if it needs rework first.',
      },
      {
        question: 'How do you keep infrastructure costs under control?',
        answer: 'By sizing compute to real load, autoscaling, using cheaper instance types where safe, and making models smaller and faster when possible.',
      },
    ],
    related: ['ai-development', 'data-engineering', 'computer-vision'],
  },
  {
    slug: 'web-development',
    category: 'product',
    visual: { shape: 'ribbon', colors: ['#1d4ed8', '#e7ff02', '#c084fc'] },
    name: 'Web Development',
    title: 'Web development',
    summary:
      'Fast, modern websites, web apps, portals and online stores — built with React and Node.js, ready for AI features from day one.',
    seoTitle: 'Web Development: Web Apps, Portals & Stores',
    seoDescription:
      'Custom web development: web applications, SaaS products, service portals and e-commerce stores built with React, Next.js and Node.js — fast, secure and SEO-ready.',
    tagline: 'Web products that are fast, findable and built to grow.',
    lead:
      'Customer portals, SaaS products, booking systems and online stores in React and Node.js — fast on phones, visible on Google and simple for your team to update.',
    statement: 'Your product, on every screen, in the browser.',
    intro: [
      'We build web applications, customer portals, booking platforms, SaaS products and online stores. A good web product loads fast, works on every device, is easy to find on Google and is simple for your team to update — and underneath, it has clean architecture that does not need rewriting when the business grows.',
      'Because we are also an AI team, we build with smart features in mind: search that understands intent, recommendations, chat assistants and automation can be added without bolting them onto a system that was never designed for them.',
    ],
    offerings: [
      { title: 'Web applications & SaaS', text: 'Custom web apps and multi-tenant SaaS products with accounts, roles, billing and dashboards.' },
      { title: 'Service & booking portals', text: 'Location search, appointment booking and customer portals — like our work for medical lab networks.' },
      { title: 'E-commerce stores', text: 'Online shops with smart search, recommendations and payment integration.' },
      { title: 'Corporate websites', text: 'Fast, SEO-optimised company websites with a CMS your team can edit.' },
      { title: 'APIs & integrations', text: 'Back-ends and integrations with payment providers, CRMs, ERPs and third-party services.' },
      { title: 'Modernisation', text: 'Rebuilding slow or outdated sites and apps on a modern, maintainable stack.' },
    ],
    useCases: [
      'You are launching a new digital product or platform',
      'Your current site is slow, hard to update or invisible on Google',
      'Customers need to book, order or manage their account online',
      'You want to add AI features to your web product',
    ],
    process: [
      { title: 'Product workshop', text: 'User types, key journeys and must-have features, turned into a sitemap and a prioritised backlog.' },
      { title: 'Design & prototype', text: 'Wireframes and a clickable prototype you can test with customers before development starts.' },
      { title: 'Build in releases', text: 'Features shipped every one or two weeks to a staging site you can click through yourself.' },
      { title: 'Launch & SEO', text: 'Go-live with analytics, performance checks, structured data and a sitemap submitted to Google.' },
    ],
    stack: ['React', 'Next.js', 'TypeScript', 'Node.js', 'Python', 'PostgreSQL', 'Stripe', 'Vercel'],
    caseStudy: 'MedLocator',
    faq: [
      {
        question: 'How long does it take to build a web application?',
        answer: 'A focused MVP usually takes 6–12 weeks; larger platforms are delivered in stages. We agree the scope and timeline before starting.',
      },
      {
        question: 'Will the website be optimised for Google?',
        answer: 'Yes. We build with fast loading, server-rendered or prerendered pages, clean URLs, structured data and correct meta tags as standard.',
      },
      {
        question: 'Can you work on our existing website?',
        answer: 'Yes. We can extend, fix or modernise an existing site, or rebuild it step by step without taking it offline.',
      },
    ],
    related: ['mobile-development', 'seo', 'ui-ux-design'],
  },
  {
    slug: 'mobile-development',
    category: 'product',
    visual: { shape: 'orb', colors: ['#6d28d9', '#e7ff02', '#38bdf8'] },
    name: 'Mobile App Development',
    title: 'Mobile app development',
    summary:
      'iOS and Android apps built with React Native or Flutter — from MVP to store release, with on-device AI when it helps.',
    seoTitle: 'Mobile App Development for iOS & Android',
    seoDescription:
      'Mobile app development for iOS and Android: cross-platform apps with React Native and Flutter, from MVP and UX design to App Store release, with on-device AI.',
    tagline: 'One codebase, both stores, a native feel.',
    lead:
      'We design, build and publish apps with React Native and Flutter, including the back-end they talk to and the store listings that help people find them.',
    statement: 'Apps people keep on their home screen.',
    intro: [
      'We build mobile apps for iOS and Android — consumer apps, companion apps for your web product, and internal tools for field teams. Most projects use cross-platform frameworks such as React Native or Flutter, which share one codebase across both platforms and cut cost and time to market without giving up a native feel.',
      'We handle the whole path: product design, development, back-end and APIs, testing on real devices, and App Store and Google Play release. Where it adds value, we bring AI into the app — camera-based recognition, smart suggestions, voice input or offline models that run on the device.',
    ],
    offerings: [
      { title: 'Cross-platform apps', text: 'iOS and Android from one React Native or Flutter codebase, with native modules where needed.' },
      { title: 'MVP development', text: 'A focused first version to test with real users quickly, built to grow rather than be thrown away.' },
      { title: 'AI-powered features', text: 'On-device image recognition, recommendations, voice and text features using mobile ML.' },
      { title: 'Back-end & APIs', text: 'The server side your app needs: accounts, data sync, notifications and payments.' },
      { title: 'Store release', text: 'App Store and Google Play submission, review guidelines, and release management.' },
      { title: 'Maintenance & updates', text: 'OS updates, crash monitoring, performance work and new features after launch.' },
    ],
    useCases: [
      'Your customers expect an app, not just a mobile website',
      'You need to reach both iOS and Android users on a sensible budget',
      'Field staff need a tool that works on the go, sometimes offline',
      'Your product idea depends on the phone’s camera, location or sensors',
    ],
    process: [
      { title: 'App concept', text: 'Core user flows, the features that make version one, and the platform decisions behind them.' },
      { title: 'Design for both platforms', text: 'Screens that respect iOS and Android conventions, tested as a prototype on real phones.' },
      { title: 'Build & beta', text: 'Development in sprints, with TestFlight and Google Play beta builds for your team and early users.' },
      { title: 'Store release', text: 'Listings, screenshots, review submission and crash monitoring from the first day live.' },
    ],
    stack: ['React Native', 'Flutter', 'TypeScript', 'Swift', 'Kotlin', 'Firebase', 'TensorFlow Lite', 'Node.js'],
    faq: [
      {
        question: 'Should we build native or cross-platform?',
        answer: 'For most business apps, cross-platform (React Native or Flutter) delivers both platforms faster and cheaper with a native feel. We recommend native only when an app needs deep platform-specific features or extreme performance.',
      },
      {
        question: 'How long does it take to build a mobile app?',
        answer: 'An MVP typically takes 8–14 weeks including design and store submission. We agree the scope first and release in stages.',
      },
      {
        question: 'Do you publish the app to the stores?',
        answer: 'Yes. We prepare the store listings, handle submission and review, and set up the accounts under your company name so you own the app.',
      },
    ],
    related: ['web-development', 'ui-ux-design', 'computer-vision'],
  },
  {
    slug: 'seo',
    category: 'product',
    visual: { shape: 'ring', colors: ['#14532d', '#e7ff02', '#38bdf8'] },
    name: 'SEO & AI Search Optimization',
    title: 'SEO and AI search optimization',
    summary:
      'Technical SEO, content and structured data that make your site easy for Google — and for AI assistants like ChatGPT and Perplexity — to find, understand and recommend.',
    seoTitle: 'SEO & AI Search Optimization Services',
    seoDescription:
      'SEO services from an engineering team: technical audits, speed and indexing fixes, structured data, multilingual SEO, content strategy and optimization for AI search.',
    tagline: 'Get found by the people — and the AI assistants — searching for what you do.',
    lead:
      'Most websites lose search traffic to problems nobody can see: pages Google cannot render, duplicate URLs, slow loading, missing structured data. We fix the engineering first, then build the content that turns searches into enquiries.',
    statement: 'SEO that starts in the code, not in a spreadsheet of keywords.',
    intro: [
      'Search engine optimization has two halves. The technical half decides whether search engines can crawl, render and understand your pages at all: server-rendered or prerendered HTML, clean URLs, correct canonical tags, sitemaps, fast loading and structured data. The content half decides whether you deserve to rank: pages that answer the questions your buyers actually search for, better than anyone else does.',
      'Search is also changing. More people now ask AI assistants and read AI-generated summaries before they click a result. Those systems favour sites that are technically clean, clearly structured and specific. We optimize for both — classic rankings and being cited in AI answers — and we measure the result in Search Console and in the enquiries you receive, not in vanity metrics.',
    ],
    offerings: [
      { title: 'Technical SEO audit', text: 'A crawl of your site covering indexing, rendering, redirects, duplicate pages, canonicals, sitemaps and robots rules, with every issue ranked by impact and effort.' },
      { title: 'Rendering & JavaScript SEO', text: 'Prerendering or server-side rendering for React, Vue and other single-page apps, so every page ships real HTML, titles and metadata to search engines and social previews.' },
      { title: 'Speed & Core Web Vitals', text: 'Faster loading, stable layouts and quick interaction: image and video optimization, code splitting, caching and font loading.' },
      { title: 'Structured data', text: 'Schema.org markup for your organization, services, articles, FAQs and products, so search engines and AI systems understand exactly what each page is about.' },
      { title: 'Content strategy & pages', text: 'Keyword and question research, a content plan, and service, industry and article pages written to answer real buyer questions — in your brand voice.' },
      { title: 'Multilingual & local SEO', text: 'Language versions search engines can index, hreflang, and local visibility for searches in Georgian, Russian, English and other languages you serve.' },
    ],
    useCases: [
      'Your site gets little search traffic even though you know people search for what you offer',
      'Your website is a single-page app and Google shows the wrong titles or none of your content',
      'Traffic dropped after a redesign, a migration or a platform change',
      'You want to appear in AI assistants’ answers, not only in classic search results',
    ],
    process: [
      { title: 'Audit & baseline', text: 'We crawl the site, review Search Console and analytics, and record today’s traffic, rankings and enquiries so progress can be measured.' },
      { title: 'Fix the foundations', text: 'Rendering, indexing, speed and structured data fixed in the code, with Search Console confirming each change.' },
      { title: 'Content that answers', text: 'New and improved pages for the services, industries and questions your buyers search for, linked together so authority flows to the pages that matter.' },
      { title: 'Measure & iterate', text: 'Monthly reports on impressions, clicks, rankings and enquiries, and a plan for what to improve next.' },
    ],
    stack: ['Google Search Console', 'Bing Webmaster Tools', 'Lighthouse', 'Schema.org', 'Prerendering / SSR', 'Next.js', 'React', 'Analytics'],
    faq: [
      {
        question: 'How long does SEO take to show results?',
        answer: 'Technical fixes are often visible in Search Console within weeks, once the affected pages are recrawled. Ranking for competitive searches usually takes several months of consistent content and improvement. We report progress monthly so you can see the trend, not just the end result.',
      },
      {
        question: 'Can you guarantee a first-page ranking?',
        answer: 'No one honestly can — search engines decide rankings, and they change their algorithms often. What we can promise is a site that search engines can fully crawl and understand, content aimed at the searches that matter to you, and transparent reporting on what changes.',
      },
      {
        question: 'What is AI search optimization?',
        answer: 'Making your site easy for AI assistants and AI-generated search summaries to understand and cite: clear, specific answers on your pages, structured data, a technically clean site, and consistent information about your company across the web.',
      },
      {
        question: 'Do you work on sites built by other agencies?',
        answer: 'Yes. We audit and fix existing sites on most platforms, including React and other JavaScript frameworks, WordPress, Webflow and custom builds, and can work alongside your current developers.',
      },
    ],
    related: ['web-development', 'ui-ux-design', 'generative-ai'],
  },
  {
    slug: 'ui-ux-design',
    category: 'strategy',
    visual: { shape: 'loop', colors: ['#9d174d', '#e7ff02', '#a78bfa'] },
    name: 'UI/UX Design',
    title: 'UI/UX design',
    summary:
      'Product design for web and mobile — research, user flows, prototypes and design systems, with interfaces that make AI understandable.',
    seoTitle: 'UI/UX Design for Web & Mobile Products',
    seoDescription:
      'UI/UX design services for web and mobile products: user research, user flows, wireframes, interactive prototypes, design systems and AI product interfaces.',
    tagline: 'Interfaces people understand the first time.',
    lead:
      'Research, user flows and Figma prototypes tested with real people before development — plus the part we know best: screens that explain what an AI is doing.',
    statement: 'Good design makes complex products feel simple.',
    intro: [
      'UX design decides how a product works — what users need, the steps they take and where they get stuck. UI design decides how it looks and feels. Together they determine whether people adopt your product or give up after the first screen, and they are far cheaper to get right in a prototype than in code.',
      'We design web and mobile products, and we specialise in a hard case: interfaces for AI. Showing a prediction with the right level of confidence, explaining why a model suggested something, and letting people correct it are design problems as much as engineering ones.',
    ],
    offerings: [
      { title: 'User research', text: 'Interviews, analytics review and usability testing to learn what users actually need.' },
      { title: 'User flows & wireframes', text: 'The structure and steps of your product, mapped before visual design starts.' },
      { title: 'Interactive prototypes', text: 'Clickable Figma prototypes to test ideas with users and stakeholders before development.' },
      { title: 'Visual UI design', text: 'Polished, accessible interfaces for web and mobile that match your brand.' },
      { title: 'Design systems', text: 'Reusable components and guidelines that keep your product consistent as it grows.' },
      { title: 'AI product UX', text: 'Designs that present predictions, confidence and explanations in a way users trust.' },
    ],
    useCases: [
      'You are starting a new product and want to validate it before building',
      'Users find your current product confusing or drop off at key steps',
      'Your AI feature works but people do not trust or use it',
      'Your product has grown inconsistent across screens and teams',
    ],
    process: [
      { title: 'Research', text: 'Interviews and analytics to learn who uses the product, what they need and where they give up.' },
      { title: 'Flows & wireframes', text: 'The structure of every key task, mapped and agreed before any visual design.' },
      { title: 'Prototype & test', text: 'An interactive Figma prototype put in front of real users, then revised on what we observe.' },
      { title: 'Design system & handover', text: 'Final screens, reusable components and specs developers can build from without guesswork.' },
    ],
    stack: ['Figma', 'FigJam', 'Maze', 'Hotjar', 'Storybook', 'Google Analytics'],
    faq: [
      {
        question: 'Do you only design, or also build?',
        answer: 'Both. You can hire us for design alone, but because our designers and developers work together, designs are built exactly as intended.',
      },
      {
        question: 'What do we receive at the end of the design phase?',
        answer: 'Figma files with all screens and states, an interactive prototype, a component library, and specifications developers can build from.',
      },
      {
        question: 'How long does a design project take?',
        answer: 'A focused product design typically takes 3–6 weeks, depending on the number of flows and the research involved.',
      },
    ],
    related: ['web-development', 'mobile-development', 'ai-consulting'],
  },
  {
    slug: 'web-design',
    category: 'strategy',
    visual: { shape: 'weave', colors: ['#0e2a47', '#e7ff02', '#22d3ee'] },
    name: 'Web Design',
    title: 'Web design that turns visitors into enquiries',
    summary:
      'Company websites, landing pages and redesigns — designed around what your buyers need to see, built fast, and ready for search from day one.',
    seoTitle: 'Web Design Services: Websites & Landing Pages',
    seoDescription:
      'Web design services: company websites, landing pages and redesigns with clear messaging, responsive layouts, fast loading, SEO built in and a CMS your team can use.',
    tagline: 'A website that looks the part and brings in work.',
    lead:
      'Most company websites are brochures: nice to look at, silent on what matters to a buyer. We design sites around the questions your customers ask, then build them fast, accessible and easy for search engines to read.',
    statement: 'Good web design is measured in enquiries, not compliments.',
    intro: [
      'Web design decides whether a visitor understands what you do in the first five seconds, trusts you enough to keep reading, and knows what to do next. That is a mix of messaging, layout, visual identity and the small details — contrast, spacing, motion — that make a site feel considered rather than assembled from a template.',
      'We design and build websites as one team, so what you approve in Figma is what goes live. Every page is responsive, loads quickly, meets accessibility basics and ships with the technical SEO a site needs to be found: clean URLs, metadata, structured data and language versions where you need them.',
    ],
    offerings: [
      { title: 'Company websites', text: 'Multi-page sites that explain what you do, show proof and make it easy to get in touch — from structure and copy to launch.' },
      { title: 'Landing pages', text: 'Focused pages for a product, campaign or market, designed around one action and built to test and improve.' },
      { title: 'Redesigns', text: 'A modern design for an existing site, keeping what works, fixing what does not and protecting your search rankings through the move.' },
      { title: 'Visual identity for the web', text: 'Typography, colour, imagery and motion that make your brand recognisable on every page and screen size.' },
      { title: 'CMS & editing', text: 'A content system your team can use — WordPress, Webflow, a headless CMS or a custom admin — so updates do not need a developer.' },
      { title: 'Multilingual sites', text: 'Language versions with their own URLs and translated metadata, so each market finds you in its own language.' },
    ],
    useCases: [
      'Your website looks dated or does not reflect the company you are today',
      'Visitors arrive but few of them get in touch',
      'You are launching a product, a new market or a campaign and need a page for it',
      'Updating the site means waiting for a developer',
    ],
    process: [
      { title: 'Goals & content', text: 'Who the site is for, what they need to see and what you want them to do; we plan the pages and write or edit the copy with you.' },
      { title: 'Design', text: 'Homepage and key templates designed in Figma, on desktop and mobile, reviewed with you before anything is built.' },
      { title: 'Build', text: 'Responsive, fast pages with your CMS, forms, analytics and technical SEO set up.' },
      { title: 'Launch & improve', text: 'Redirects, Search Console and a launch checklist, then changes based on how real visitors use the site.' },
    ],
    stack: ['Figma', 'React', 'Next.js', 'WordPress', 'Webflow', 'Headless CMS', 'Google Analytics', 'Search Console'],
    caseStudy: 'Medical Tourism',
    faq: [
      {
        question: 'How long does a new website take?',
        answer: 'A landing page usually takes one to two weeks. A company website of five to fifteen pages typically takes three to six weeks, depending on how much content needs writing and how many languages it has.',
      },
      {
        question: 'Can you write the text as well?',
        answer: 'Yes. We plan the pages and write or edit the copy with you, so the design is built around real content rather than placeholder text.',
      },
      {
        question: 'Will we be able to update the site ourselves?',
        answer: 'Yes. We set up a content system suited to your team and show you how to use it, so text, images and new pages do not need a developer.',
      },
      {
        question: 'Will a redesign hurt our Google rankings?',
        answer: 'Not if it is done carefully. We keep or redirect every existing URL, carry over titles and metadata, and check Search Console after launch so rankings are protected through the move.',
      },
    ],
    related: ['ui-ux-design', 'web-development', 'seo'],
  },
  {
    slug: 'nearshore-ai-development-europe',
    category: 'strategy',
    visual: { shape: 'ring', colors: ['#1e3a8a', '#e7ff02', '#fde047'] },
    name: 'Nearshore AI Development for Europe',
    title: 'Nearshore AI and software development for European companies',
    summary:
      'A senior AI, web and mobile team two to three hours ahead of Central Europe — GDPR-ready delivery, EU AI Act documentation and code you own.',
    seoTitle: 'Nearshore AI Development for European Companies',
    seoDescription:
      'Nearshore AI, web and mobile development for European companies: a team in Tbilisi working your hours, GDPR-ready with a DPA and EU hosting, EU AI Act aware.',
    tagline: 'Your working day, your data rules, a team that ships.',
    lead:
      'European companies hire us when the AI feature on the roadmap has no one to build it. We work inside your working day, keep personal data in the EU, document what the EU AI Act asks for, and hand over code and models you own outright.',
    statement: 'Close enough to work together. Far enough to cost less.',
    intro: [
      'Hiring machine learning engineers in Western Europe is slow and expensive, and far-offshore teams answer your questions while you sleep. Tbilisi sits in between: 2–3 hours ahead of Central European time all year, so your whole working day overlaps ours, and direct flights connect Georgia with many EU capitals in a few hours when a workshop in person helps.',
      'What European buyers usually worry about is not code — it is compliance and control. We design for GDPR from the first sprint: personal data stays in EU-region cloud hosting, access is limited and logged, and we sign a data processing agreement with the EU Standard Contractual Clauses. For AI systems we prepare what the EU AI Act expects of a provider: a risk classification, technical documentation, human oversight and transparency for users.',
    ],
    offerings: [
      { title: 'Working-hours overlap', text: 'Daily stand-ups, workshops and calls inside the Central European working day, in English — with Georgian and Russian available too.' },
      { title: 'GDPR by design', text: 'EU-region hosting, data minimisation, access logs and a DPA with Standard Contractual Clauses, so personal data never has to leave the EU.' },
      { title: 'EU AI Act readiness', text: 'Risk classification of your AI use case, technical documentation, human-oversight design and user transparency notices.' },
      { title: 'Dedicated team or fixed scope', text: 'A team that joins your sprints and tools, or a fixed-price project with a written scope, milestones and a success metric.' },
      { title: 'You own everything', text: 'Source code, trained model weights, data pipelines and documentation are yours. NDA signed before any details change hands.' },
      { title: 'Clear commercial terms', text: 'Quotes in EUR or USD, milestone-based payment, and a free 30-minute scoping call before anything is signed.' },
    ],
    useCases: [
      'An AI feature is on your roadmap and your in-house team has no machine learning engineer',
      'Local agencies quoted a price or a timeline you cannot accept',
      'An earlier offshore team was hard to reach during your working day',
      'Your data protection officer needs GDPR and EU AI Act questions answered before any code is written',
    ],
    process: [
      { title: 'Free scoping call', text: 'Thirty minutes on your goal, your data and your constraints — including where personal data may and may not go.' },
      { title: 'Written plan & DPA', text: 'Scope, milestones, success metric and price in writing, with the NDA and data processing agreement signed before any data is shared.' },
      { title: 'Prototype in 3–6 weeks', text: 'A working prototype on your data, demoed in your working hours, with the compliance documentation growing alongside it.' },
      { title: 'Production & handover', text: 'Deployment into your cloud or ours in an EU region, monitoring, documentation and a full handover to your team.' },
    ],
    stack: ['GDPR', 'EU AI Act', 'DPA + SCCs', 'AWS / Azure / GCP EU regions', 'Python', 'PyTorch', 'React', 'Node.js'],
    faq: [
      {
        question: 'What time zone does the team work in?',
        answer: 'Tbilisi is on GMT+4 all year, which is 2 hours ahead of Central European Summer Time and 3 hours ahead in winter. A 10:00 call in Berlin, Paris or Warsaw is early afternoon for us, so your whole working day overlaps ours.',
      },
      {
        question: 'Is working with a team in Georgia GDPR-compliant?',
        answer: 'Yes, with the right setup. Georgia is outside the EU, so we sign a data processing agreement that includes the EU Standard Contractual Clauses, and we host personal data in EU-region cloud infrastructure so it does not need to leave the EU. Wherever possible we work with anonymised or synthetic data.',
      },
      {
        question: 'Do you help with the EU AI Act?',
        answer: 'Yes. We classify the risk level of your AI use case, and for systems that need it we build in human oversight, logging and transparency for users, and prepare the technical documentation. For formal legal sign-off we work alongside your legal adviser.',
      },
      {
        question: 'How do contracts and payments work?',
        answer: 'We quote in EUR or USD, either as a fixed price per milestone or as a monthly dedicated team. An NDA is signed before any details are shared, and the code and models are handed over to you in full.',
      },
      {
        question: 'Can we meet the team in person?',
        answer: 'Yes. We are happy to run the kickoff workshop at your office, or to host you in Tbilisi, which has direct flights from many European cities.',
      },
    ],
    related: ['ai-consulting', 'ai-development', 'generative-ai'],
  },
  {
    slug: 'ai-consulting',
    category: 'strategy',
    visual: { shape: 'ribbon', colors: ['#1e1b4b', '#e7ff02', '#60a5fa'] },
    name: 'AI Consulting & Strategy',
    title: 'AI consulting & strategy',
    summary:
      'An honest assessment of where AI will pay off in your business, a prioritised roadmap, and the first project scoped and ready to build.',
    seoTitle: 'AI Consulting & AI Strategy Services',
    seoDescription:
      'AI consulting services: AI readiness assessment, use-case discovery, feasibility studies, data audits and a prioritised AI roadmap focused on measurable business value.',
    tagline: 'Know where AI pays off before you spend on it.',
    lead:
      'A few weeks of interviews, data review and estimates that leave you with a ranked list of AI opportunities and one project scoped tightly enough to start.',
    statement: 'Start with the right problem.',
    intro: [
      'Almost every business can use AI somewhere, but not everywhere is worth it. AI consulting answers the practical questions first: which processes would benefit most, whether your data can support them, what it would cost, what return to expect, and what could go wrong — so investment goes into the projects with the clearest payoff.',
      'Our advice comes from engineers who build these systems, not from slide decks. The result is concrete: a ranked list of opportunities, a realistic roadmap, and the first project scoped in enough detail to start building. If the honest answer is that AI is not the right tool, we will say so.',
    ],
    offerings: [
      { title: 'AI readiness assessment', text: 'A structured review of your processes, data and team to find where AI fits today.' },
      { title: 'Use-case discovery', text: 'Workshops with your team to identify, estimate and rank AI opportunities by value and effort.' },
      { title: 'Feasibility & proof of concept', text: 'A short technical study or prototype that proves an idea works on your data before full investment.' },
      { title: 'Data audit', text: 'An assessment of the quality, access and gaps in the data your AI plans depend on.' },
      { title: 'AI roadmap', text: 'A phased plan with priorities, budget ranges, timelines and success metrics.' },
      { title: 'Vendor & build-vs-buy advice', text: 'Independent guidance on whether to buy an off-the-shelf tool or build custom.' },
    ],
    useCases: [
      'Leadership wants an AI plan but it is unclear where to begin',
      'You have many AI ideas and need to know which to fund first',
      'You are evaluating AI vendors and want an independent technical view',
      'A previous AI initiative stalled and you want to understand why',
    ],
    process: [
      { title: 'Interviews', text: 'Conversations with the people who run the processes, to understand where time and money go.' },
      { title: 'Data & systems review', text: 'A look at the data and tools involved, to judge what is technically feasible.' },
      { title: 'Opportunity ranking', text: 'Each idea estimated for value, effort, risk and data readiness, then ranked.' },
      { title: 'Roadmap & first project', text: 'A phased roadmap and a detailed scope for the first project, ready to build.' },
    ],
    stack: ['Workshops', 'Data audits', 'Proofs of concept', 'ROI modelling', 'Roadmapping'],
    faq: [
      {
        question: 'What do we get from an AI consulting engagement?',
        answer: 'A ranked list of AI opportunities with estimated value and effort, a phased roadmap, and a detailed scope for the first project. Optionally, a working proof of concept.',
      },
      {
        question: 'How long does an AI assessment take?',
        answer: 'Typically 2–4 weeks, depending on the size of the business and how many processes are in scope.',
      },
      {
        question: 'Is there a quick way to check if we are ready for AI?',
        answer: 'Yes — our free AI readiness check gives you a first view in a few minutes, and the discovery call that follows is free and without obligation.',
      },
    ],
    related: ['ai-development', 'data-engineering', 'ai-agents'],
  },
]

export const getService = (slug) => services.find((service) => service.slug === slug)

export default services
